// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — talking with the AYNI chatbot (AYNI 3D's `docs/AI_CHATBOT_VOICE.md`),
 * on the page and in the headset. The voice mode itself is AYNI 3D's
 * (`src/ayni3d/renderer/ui/ai-voice-mode.ts`, copied unchanged): one tap
 * starts it, the mic hears when the question is over, the words go in as an
 * ordinary question (the panel draws it), and the reply is read aloud; then
 * it listens again, until it is tapped off or nobody speaks. A tap while it
 * speaks interrupts it.
 *
 * The words become text on this site's own route (`/api/ayni-chat/audio/
 * transcriptions`); the reply is read by the route's voice (English and
 * Spanish) or, for another language or when that fails, by the device's own
 * voice; with neither, the captions still show. The headset draws the phase,
 * the question and the reply (`vrHud.ts` VrVoiceState, `vrChatPanel.ts`), so
 * the chatbot works in VR, where there is no keyboard.
 *
 * AYNI (owner on Quest, 2026-11-04: questions asked in Spanish were "not
 * properly translated to English and the response was in English"): the
 * recording goes without a language, so the speech model hears which one it
 * is instead of being held to the interface's (Whisper, held to English,
 * turned Spanish into English words). A turn spoken in English, Spanish or
 * Catalan is answered and read aloud in it; another language falls back to
 * the interface's. And ("the answer is just typed, not read"): any failure of
 * the route's voice, the day's allowance included, hands the reading to the
 * device's own voice; when no voice can read it, the headset says so. The
 * replies play through one audio output the mic's press unlocks
 * (`xrSpeechOut.ts`): a browser that lets sound play only just after a press
 * refused AYNI 3D's player, which opens a new one for every sentence.
 */
import { VoiceMode, type VoiceEndReason, type VoicePhase } from '../ayni3d/renderer/ui/ai-voice-mode'
import { DeviceVoice, SpeechRecorder, isPhantomTranscript, speechInputSupported } from '../ayni3d/renderer/ui/ai-speech-io'
import { toSpeakableText } from '../ayni3d/shared/ai-speakable-text'
import { normalizeAiChatLanguage, type AiChatLanguage } from '../ayni3d/shared/ai-languages'
import type { VrVoiceState } from '../services/vrHud'
import { AYNI_CHAT_BASE_URL, XR_CHAT_LANGUAGES } from './xrChatScope'
import { UnlockedSpeech } from './xrSpeechOut'

export interface AyniChatVoiceHost {
  language(): AiChatLanguage
  /** Whether this account may talk to the chatbot now. */
  allowed(): boolean
  /** Asks the question as an ordinary turn (the panel draws it), answered in `language`: the reply's words, or null. */
  ask(text: string, language: AiChatLanguage): Promise<string | null>
  /** Stops the answer in progress. */
  cancel(): void
  /** The phase changed: the panel's mic and status line. */
  onPhase?(phase: VoicePhase, detail?: { ended?: VoiceEndReason; code?: string }): void
  fetchImpl?: typeof fetch
  /** For tests. */
  now?(): number
}

export interface AyniChatVoice {
  /** Whether this browser can record (a secure page with a mic). */
  readonly supported: boolean
  tap(): void
  stop(): void
  phase(): VoicePhase
  /** What the headset's dock shows: null hides its mic. */
  vrState(): VrVoiceState | null
}

/** How long a finished reply stays on the dock's caption strip, and an error's notice. */
const REPLY_LINGER_MS = 10_000
const ERROR_LINGER_MS = 5_000

/** The route's answer as the engine's error codes. */
function codeFor(status: number): string {
  if (status === 429) return 'AI_QUOTA'
  if (status === 401) return 'AI_NOT_CONFIGURED'
  return 'AI_UNAVAILABLE'
}

/** Whisper's names for the languages this site answers in, besides their codes. */
const SPOKEN_NAMES: Readonly<Record<string, AiChatLanguage>> = { english: 'en', spanish: 'es', castilian: 'es', catalan: 'ca', valencian: 'ca' }

/**
 * The language a turn is answered in: the one the question was spoken in
 * when this site answers in it, else the interface's.
 */
export function spokenLanguage(heard: unknown, fallback: AiChatLanguage): AiChatLanguage {
  if (typeof heard !== 'string' || !heard.trim()) return fallback
  const named = SPOKEN_NAMES[heard.trim().toLowerCase()]
  const language = named ?? normalizeAiChatLanguage(heard, fallback)
  return (XR_CHAT_LANGUAGES as readonly string[]).includes(language) ? language : fallback
}

export function createAyniChatVoice(host: AyniChatVoiceHost): AyniChatVoice {
  const fetchImpl: typeof fetch = host.fetchImpl ?? ((input, init) => fetch(input, init))
  const now = (): number => host.now?.() ?? Date.now()
  const recorder = new SpeechRecorder()
  const device = typeof window !== 'undefined' && 'speechSynthesis' in window ? new DeviceVoice() : null
  let question = ''
  let reply = ''
  /** The question the reply answers (the mic clears `question` when it listens again). */
  let replyQuestion = ''
  let replyAt = 0
  let ended: { reason: VoiceEndReason; at: number } | null = null
  /** The language this turn is answered and read in (the one it was spoken in). */
  let turnLanguage: AiChatLanguage = host.language()
  /** No voice could read the reply out (the headset says so). */
  let unread = false

  const player = new UnlockedSpeech(async (text, language) => {
    try {
      const res = await fetchImpl(`${AYNI_CHAT_BASE_URL}/audio/speech`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input: toSpeakableText(text, language) || text, language }),
      })
      // Any refusal, the day's allowance included, lets the device's own voice read it instead.
      if (!res.ok) return { ok: false, code: 'AI_UNAVAILABLE', message: String(res.status) }
      return { ok: true, audio: new Uint8Array(await res.arrayBuffer()) }
    } catch {
      return { ok: false, code: 'AI_UNAVAILABLE', message: 'unreachable' }
    }
  }, () => {
    // No voice at all: the captions and the panel still carry the reply, and the headset says it was not read.
    unread = true
  }, device)

  const mode = new VoiceMode({
    record: (onEnd) => {
      player.stop()
      return recorder.start({ onLimit: () => onEnd('speech-end'), onEnd })
    },
    stopRecording: () => recorder.stop(),
    cancelRecording: () => recorder.cancel(),
    transcribe: async (wav) => {
      // No language: the model hears which one it is (held to the interface's, it translated).
      const form = new FormData()
      form.append('file', new Blob([new Uint8Array(wav)], { type: 'audio/wav' }), 'question.wav')
      try {
        const res = await fetchImpl(`${AYNI_CHAT_BASE_URL}/audio/transcriptions`, { method: 'POST', credentials: 'same-origin', body: form })
        if (!res.ok) return { ok: false, code: codeFor(res.status) }
        const body = (await res.json()) as { text?: unknown; language?: unknown }
        turnLanguage = spokenLanguage(body.language, host.language())
        return { ok: true, text: typeof body.text === 'string' ? body.text : '' }
      } catch {
        return { ok: false, code: 'AI_UNAVAILABLE' }
      }
    },
    ask: async (text) => {
      question = text
      reply = ''
      unread = false
      const answer = await host.ask(text, turnLanguage)
      if (answer) {
        reply = answer
        replyQuestion = text
        replyAt = now()
      }
      return answer
    },
    speak: async (text) => {
      if (player.canSpeak(turnLanguage)) await player.say(text, turnLanguage)
      else unread = true
      replyAt = now()
    },
    stopSpeaking: () => player.stop(),
    cancelTurn: () => host.cancel(),
    isNoise: (text) => isPhantomTranscript(text),
    onPhase: (phase, detail) => {
      if (phase === 'listening') {
        question = ''
        ended = null
      }
      if (detail?.ended && detail.ended !== 'quiet') ended = { reason: detail.ended, at: now() }
      host.onPhase?.(phase, detail)
    },
  }, { handsFree: true })

  const supported = speechInputSupported()
  return {
    supported,
    tap: () => {
      if (!supported || !host.allowed()) return
      // Inside the press: the reply, seconds later, plays through what this unlocks.
      player.unlock()
      mode.tap()
    },
    stop: () => mode.stop(),
    phase: () => mode.phase,
    vrState: () => {
      if (!supported || !host.allowed()) return null
      switch (mode.phase) {
        case 'listening': {
          // Hands-free, it listens again after a reply: the reply stays readable meanwhile.
          const lingering = reply && now() - replyAt < REPLY_LINGER_MS
          return lingering ? { phase: 'listening', caption: reply, question: replyQuestion, ...(unread ? { unread } : {}) } : { phase: 'listening', caption: '' }
        }
        case 'transcribing':
          return { phase: 'thinking', caption: '' }
        case 'thinking':
          return { phase: 'thinking', caption: question }
        case 'speaking':
          return { phase: 'speaking', caption: reply, question: replyQuestion, ...(unread ? { unread } : {}) }
        case 'off': {
          if (ended && now() - ended.at < ERROR_LINGER_MS) return { phase: 'error', caption: '' }
          const lingering = reply && now() - replyAt < REPLY_LINGER_MS
          return lingering ? { phase: 'idle', caption: reply, question: replyQuestion, ...(unread ? { unread } : {}) } : { phase: 'idle', caption: '' }
        }
      }
    },
  }
}
